// The read model. Lanes (lanes.ts) store evidence in the shape it was
// produced — one lane per sealed rendition, indexed exactly as
// `SegmentVerifier` reported it. Nothing that draws reads that directly:
// every surface reads the projection this module produces, onto the
// CANONICAL lane's grid (`root.manifest.renditions[0]`, deterministic
// because the sealed order is fixed).
//
// Pure, and cheap enough to run on every LaneSet change. See the "Composite:
// the read model" section of docs/plans/2026-09-07-verification-overlay.md.
//
// Two rules govern the whole file:
//
//   1. Cells are INDEPENDENT. There is no prefix, watermark or "we got this
//      far". Seeking produces genuine islands of checked video with genuine
//      holes between them and this projection draws exactly that. The only
//      claim ever made is: these specific slices were checked, those were not.
//   2. Precedence per cell is mismatch > verified > unchecked, whichever lane
//      each came from. A lane that disagreed with the seal is never masked by
//      another lane that agreed.
import type { Lane, LaneSet, LaneSwitch, SliceState } from './lanes.js'

export type CellState = 'unchecked' | 'verified' | 'mismatch'

export interface CompositeCell {
  index: number
  start: number
  end: number
  state: CellState
  /** Lanes that verified this span; length > 1 means it was checked against
   * more than one sealed manifest. Present on a `mismatch` cell too — it
   * names what agreed, alongside what did not. */
  by: string[]
  /**
   * The fingerprint the first agreeing lane recorded for this span, if one is
   * available. This is what the chart prints above a column as it is drawn —
   * the moment the plan's Architecture §6 calls the one bold element.
   *
   * Optional for a real reason, not as a hedge: on a RAGGED ladder a canonical
   * cell has no single lane slice to name, so there is no one hash that
   * honestly belongs to it and this stays undefined. The chart then draws the
   * column without a tag. An unchecked cell never has one.
   */
  hash?: string
}

export interface CompositeSummary {
  cells: CompositeCell[]
  /** Distinct cells that were looked at (verified or mismatched), never a
   * count of results: a span checked by two lanes counts once. */
  checkedCount: number
  totalCount: number
  /** The earliest cell that any lane disagreed about, or null. A
   * whole-playlist mismatch (no `segmentIndex`) marks no cell and so leaves
   * this null while `LaneSet.mismatch` is still set — the glyph reads that,
   * not this. */
  mismatchIndex: number | null
  /** Carried straight through from `LaneSet.switches`. This is their ONLY
   * route into the chart — nothing downstream of here sees a LaneSet. */
  switches: LaneSwitch[]
  /** `LaneSet.active`, for the modal footer naming the level being checked. */
  active: string | null
  /** `LaneSet.checkedBytes`, for the panel's running count. */
  checkedBytes: number
  /** `LaneSet.checkedSeconds` and `LaneSet.checkedPixels`, likewise. */
  checkedSeconds: number
  checkedPixels: number
}

const UNCHECKED: SliceState = 0
const VERIFIED: SliceState = 1
const MISMATCH: SliceState = 2

/** Shared for every unchecked cell — the common case by far, and this keeps a
 * 900-cell projection from allocating 900 empty arrays every 4 seconds.
 * Frozen because a caller must never treat `by` as its own scratch space. */
const NO_LANES: string[] = []
Object.freeze(NO_LANES)

/** Overlap smaller than this is float noise from summing durations, not a
 * real intersection: a lane boundary that lands exactly on a canonical one
 * must not leak into the neighbouring cell. Not a tolerance policy — that is
 * `DURATION_TOLERANCE_SECONDS`, and it belongs to the alignment decision
 * `buildLanes` already made. */
const OVERLAP_EPSILON = 1e-6

/** Greatest index whose start is at or before `time`, or 0. */
function floorIndex(starts: Float64Array, time: number): number {
  let lo = 0
  let hi = starts.length - 1
  let best = 0
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (starts[mid] <= time) {
      best = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return best
}

/** Aligned path: the lane's grid is the canonical grid, so position i in one
 * is position i in the other. O(1). */
function directState(lane: Lane, index: number): SliceState {
  if (index >= lane.states.length) return UNCHECKED
  return lane.states[index] as SliceState
}

/** Ragged path: binary-search the lane's cumulative starts and take any slice
 * overlapping the canonical span, under the same precedence as everywhere
 * else. A coarser lane therefore covers several canonical cells, and a finer
 * one contributes several slices to one — busier, never wrong. */
function overlapState(lane: Lane, start: number, end: number): SliceState {
  if (lane.starts.length === 0) return UNCHECKED
  let held: SliceState = UNCHECKED
  for (let i = floorIndex(lane.starts, start); i < lane.starts.length; i++) {
    const sliceStart = lane.starts[i]
    if (sliceStart >= end - OVERLAP_EPSILON) break
    if (sliceStart + lane.durations[i] <= start + OVERLAP_EPSILON) continue
    const state = lane.states[i] as SliceState
    if (state === MISMATCH) return MISMATCH
    if (state === VERIFIED) held = VERIFIED
  }
  return held
}

/**
 * Projects a LaneSet onto the canonical lane's grid. Pure: the same LaneSet
 * always yields the same summary, so callers may memoize on the LaneSet
 * reference (which `applyResult` only changes when something actually
 * changed).
 */
export function composite(set: LaneSet): CompositeSummary {
  const canonical = set.lanes.get(set.canonical)
  if (!canonical) {
    return {
      cells: [],
      checkedCount: 0,
      totalCount: 0,
      mismatchIndex: null,
      switches: set.switches,
      active: set.active,
      checkedBytes: set.checkedBytes,
      checkedSeconds: set.checkedSeconds,
      checkedPixels: set.checkedPixels,
    }
  }

  const lanes = Array.from(set.lanes.values())
  const totalCount = canonical.states.length
  const cells: CompositeCell[] = new Array(totalCount)
  let checkedCount = 0
  let mismatchIndex: number | null = null

  for (let index = 0; index < totalCount; index++) {
    const start = canonical.starts[index]
    const end = start + canonical.durations[index]

    let state: SliceState = UNCHECKED
    let by: string[] = NO_LANES
    // First agreeing lane wins the fingerprint, in lane order — deterministic,
    // and the canonical lane is first in the Map, so it is preferred.
    let hash: string | undefined

    for (const lane of lanes) {
      // The canonical lane is its own grid whether or not the ladder is
      // aligned, so it never needs the search path.
      const held =
        set.aligned || lane === canonical ? directState(lane, index) : overlapState(lane, start, end)

      if (held === MISMATCH) {
        state = MISMATCH
      } else if (held === VERIFIED) {
        if (state === UNCHECKED) state = VERIFIED
        // Lane names are unique Map keys, so no lane can be named twice.
        if (by === NO_LANES) by = [lane.rendition]
        else by.push(lane.rendition)
        // Only the direct path can name the slice this cell IS; the overlap
        // path may have merged several, so it contributes no fingerprint.
        if (hash === undefined && (set.aligned || lane === canonical)) {
          hash = lane.hashes.get(index)
        }
      }
    }

    if (state !== UNCHECKED) checkedCount++
    if (state === MISMATCH && mismatchIndex === null) mismatchIndex = index

    cells[index] = {
      index,
      start,
      end,
      state: state === MISMATCH ? 'mismatch' : state === VERIFIED ? 'verified' : 'unchecked',
      by,
      hash,
    }
  }

  return {
    cells,
    checkedCount,
    totalCount,
    mismatchIndex,
    switches: set.switches,
    active: set.active,
    checkedBytes: set.checkedBytes,
    checkedSeconds: set.checkedSeconds,
    checkedPixels: set.checkedPixels,
  }
}
